import { Survey, SearchResponse, SurveyById, SurveyData, Commodities } from "../../interfaces/dataInterfaces"
import { blobToDownload, transformResponse } from "../../utils/helpers"
import { rpmeApi } from "./rpmeApi"

export const survey = rpmeApi.injectEndpoints({
  endpoints: builder => ({
    getSurveyRPMEList: builder.query<
      SearchResponse<Survey>,
      { skip: number; top: number; adm0Code: number; search: string }
    >({
      query: query => ({
        url: `Survey/Get`,
        params: query,
        method: "GET",
      }),
      providesTags: ["SurveysRPME"],
    }),

    saveSurveyRPMEFile: builder.mutation<{ fileName: string }, FormData>({
      query: body => ({
        url: "Survey/SaveCSVFile",
        method: "POST",
        body,
      }),
    }),

    validateSurveyRPME: builder.query<
      { isValid: boolean; validationResult: string },
      { fileName: string; adm0Code: number; xlsFormID: number }
    >({
      query: query => ({
        url: `Survey/ValidateSurvey`,
        params: query,
        method: "GET",
      }),
    }),

    validateSurveyRPMEMinRequiredFields: builder.query<
      { isValid: boolean; validationResult: string; validationToken: string },
      { fileName: string; adm0Code: number; xlsFormID: number }
    >({
      query: query => ({
        url: `Survey/ValidateSurveyForMinimumRequiredFields`,
        params: query,
        method: "GET",
      }),
    }),

    saveSurveyRPME: builder.mutation<{ isSuccess: boolean; resultMessage: string; surveyId: number }, SurveyData>({
      query: body => ({
        url: "Survey/Save",
        method: "POST",
        body,
      }),
      invalidatesTags: ["SurveysRPME"],
    }),

    getSurveyRPME: builder.query<SurveyById, number>({
      query: id => ({
        url: `Survey/Get/${id}`,
        method: "GET",
      }),
    }),

    deleteSurveyRPME: builder.mutation<void, number>({
      query: id => ({
        url: `Survey/Delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SurveysRPME"],
    }),

    saveRpmeBasketRPME: builder.mutation<
      { resultMessage: string; resultCode: number },
      { file: FormData; surveyId: number; priceCompare: number }
    >({
      query: ({ file, surveyId, priceCompare }) => ({
        url: `Survey/SaveRpmeBasket`,
        method: "POST",
        params: { surveyId, priceCompare },
        body: file,
      }),
    }),

    listCommodityByBasketRPME: builder.query<Commodities[], { surveyId: number }>({
      query: query => ({
        url: `Survey/GetListCommodityBasket`,
        params: query,
        method: "GET",
      }),
    }),

    downloadSurveyFileRPME: builder.mutation<void, { dataType: string; surveyId: number; format: string }>({
      query: ({ dataType, surveyId, format }) => {
        let url = `/Admin/Srvc/Proxy?u=apiget/RPME/${dataType}?surveyID=${surveyId}&xlsFormType=rpme`
        if (format) url += `&format=${format}`
        return {
          url,
          responseHandler: async (response: Response) => {
            const res = await transformResponse(response, format)
            return res
          },
        }
      },
    }),

    downloadOriginSurveyRPME: builder.mutation<void, { surveyId?: number; file?: string }>({
      query: ({ file, surveyId }) => {
        return {
          url: `Survey/DownloadOriginSurvey`,
          params: { surveyId, surveyOriginalFilename: file },
          method: "GET",
          responseHandler: async response => blobToDownload(response, `${file?.split(".csv_")[0]}.csv`),
        }
      },
    }),
  }),
})

export const {
  useGetSurveyRPMEListQuery,
  useSaveSurveyRPMEFileMutation,
  useValidateSurveyRPMEQuery,
  useValidateSurveyRPMEMinRequiredFieldsQuery,
  useSaveSurveyRPMEMutation,
  useGetSurveyRPMEQuery,
  useDeleteSurveyRPMEMutation,
  useListCommodityByBasketRPMEQuery,
  useDownloadSurveyFileRPMEMutation,
  useDownloadOriginSurveyRPMEMutation,
  useSaveRpmeBasketRPMEMutation,
} = survey
